import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getEmployee,
  getLeader,
  updateSalary,
  viewAllSalaries,
} from "../../http";
import { toast } from "react-toastify";
import Loading from "../Loading";
import HeaderSection from "../../components/HeaderSection";

const SalaryView = () => {
  const { id } = useParams();
  const [salary, setSalary] = useState();
  const [employee, setEmployee] = useState();
  // TDS options
  const [isTdsApplicable, setIsTdsApplicable] = useState(false);

  // Initialize with empty strings, but we'll populate from existing data
  const initialState = {
    basic: "",
    hra: "",
    medicalAllowance: "",
    conveyanceAllowance: "",
    bookAllowance: "",
    specialPeriodicAllowance: "",
    employerPf: "",
    pli: "",
    others: "",
    totalLeaves: "",
    tds: "",
    otherDeduction: "",
    loanAdvance: "",
    totalDeduction: "",
    pt: "",
  };
  const [formData, setFormData] = useState(initialState);

  useEffect(() => {
    console.log("Received ID in SalaryView:", id);
    const fetchData = async () => {
      try {
        // First fetch all salaries for the given ID
        const obj = { employeeID: id };
        const salaryRes = await viewAllSalaries(obj);

        // Check if we have salary data
        if (!salaryRes.data || salaryRes.data.length === 0) {
          console.error("No salary data found");
          toast.error("No salary information found for this employee");
          return;
        }

        const salaryData = salaryRes.data[0];
        console.log("Salary Data:", salaryData);

        // Fetch employee and leader info concurrently
        const [empRes, leaderRes] = await Promise.all([
          getEmployee(salaryData.employeeID),
          getLeader(salaryData.employeeID),
        ]);

        // Set employee data based on role
        if (empRes.success) {
          setEmployee(empRes.data);
        } else if (leaderRes.success) {
          setEmployee(leaderRes.data);
        } else {
          console.error(
            "Employee data not found in either employee or leader response"
          );
          toast.error("Employee information not found");
          return;
        }

        // Set salary data
        setSalary(salaryData);

        // Update form data if salary information exists
        setFormData({
          basic: salaryData.basic || "",
          hra: salaryData.hra || "",
          medicalAllowance: salaryData.medicalAllowance || "",
          conveyanceAllowance: salaryData.conveyanceAllowance || "",
          bookAllowance: salaryData.bookAllowance || "",
          specialPeriodicAllowance: salaryData.specialPeriodicAllowance || "",
          employerPf: salaryData.employerPf || "",
          pli: salaryData.pli || "",
          others: salaryData.others || "",
          totalLeaves: salaryData.totalLeaves || "",
          employeePf: salaryData.employeePf || "",
          tds: salaryData.tds || "",
          pt: salaryData.pt || "",
          otherDeduction: salaryData.otherDeduction || "",
          loanAdvance: salaryData.loanAdvance || "",
          totalDeduction: salaryData.totalDeduction || "",
        });

        // Set TDS checkbox state
        setIsTdsApplicable(salaryData.tds > 0);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error(
          `Error: ${error.message || "Failed to fetch salary information"}`
        );
      }
    };

    // Only fetch if we have an ID
    if (id) {
      fetchData();
    }
  }, [id]);

  const inputEvent = (e) => {
    const { name, value } = e.target;
    setFormData((old) => {
      const newFormData = { ...old, [name]: value };
      // Auto-calculate totalDeduction whenever relevant fields change
      if (
        [
          "employeePf",
          "tds",
          "otherDeduction",
          "loanAdvance",
          "pt",
          "employerPf",
        ].includes(name)
      ) {
        const employeePf = parseFloat(newFormData.employeePf) || 0;
        const tds = parseFloat(newFormData.tds) || 0;
        const otherDeduction = parseFloat(newFormData.otherDeduction) || 0;
        const loanAdvance = parseFloat(newFormData.loanAdvance) || 0;
        const pt = parseFloat(newFormData.pt) || 0;
        const employerPf = parseFloat(newFormData.employerPf) || 0;
        newFormData.totalDeduction = (
          employeePf +
          tds +
          otherDeduction +
          loanAdvance +
          employerPf +
          pt
        ).toString();
      }
      return newFormData;
    });
  };

  // Handle TDS checkbox change
  const handleTdsChange = (e) => {
    const isChecked = e.target.checked;
    setIsTdsApplicable(isChecked);

    // If TDS is not applicable, set it to 0
    if (!isChecked) {
      setFormData((prev) => {
        const newData = { ...prev, tds: "0" };
        // Recalculate total deduction
        const employeePf = parseFloat(newData.employeePf) || 0;
        const otherDeduction = parseFloat(newData.otherDeduction) || 0;
        const loanAdvance = parseFloat(newData.loanAdvance) || 0;
        const pt = parseFloat(newData.pt) || 0;
        const employerPf = parseFloat(newData.employerPf) || 0;
        newData.totalDeduction = (
          employeePf +
          otherDeduction +
          loanAdvance +
          employerPf +
          pt
        ).toString();
        return newData;
      });
    }
  };

  const calculateTotalDeduction = () => {
    const employeePf = parseFloat(formData.employeePf) || 0;
    const employerPf = parseFloat(formData.employerPf) || 0;
    const tds = parseFloat(formData.tds) || 0;
    const otherDeduction = parseFloat(formData.otherDeduction) || 0;
    const loanAdvance = parseFloat(formData.loanAdvance) || 0;
    const pt = parseFloat(formData.pt) || 0;

    return employeePf + employerPf + tds + otherDeduction + loanAdvance + pt;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Ensure employee ID is included
    const updatedData = {
      ...formData,
      employeeID: employee.id,
      // Ensure totalDeduction is calculated correctly before submission
      totalDeduction: calculateTotalDeduction(),
    };

    // console.log(updatedData);

    const res = await updateSalary(updatedData);
    if (res.success) {
      toast.success("Salary Updated!");
      // Refresh the salary data after update
      const refreshRes = await viewAllSalaries({ _id: id });
      if (refreshRes.data && refreshRes.data[0]) {
        setSalary(refreshRes.data[0]);
      }
    } else {
      toast.error("Failed to update salary!");
    }
  };

  const getSavedPayableSalary = () => {
    if (!salary) return "";

    const totalEarnings =
      parseFloat(salary.basic || 0) +
      parseFloat(salary.hra || 0) +
      parseFloat(salary.medicalAllowance || 0) +
      parseFloat(salary.conveyanceAllowance || 0) +
      parseFloat(salary.bookAllowance || 0) +
      parseFloat(salary.specialPeriodicAllowance || 0) +
      parseFloat(salary.pli || 0) +
      parseFloat(salary.employeePf || 0) +
      parseFloat(salary.others || 0);

    const totalDeductions = parseFloat(salary.totalDeduction || 0);

    return (totalEarnings - totalDeductions).toFixed(2);
  };
  return (
    <>
      {employee ? (
        <div className="main-content">
          <section className="section">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h4>Updated Salary from {salary?.assignedDate}</h4>
              </div>
            </div>

            <div className="col-md-9">
              <table className="table">
                <tbody>
                  <tr>
                    <th>Name</th>
                    <td>{employee?.name}</td>
                  </tr>
                  <tr>
                    <th>Email</th>
                    <td>{employee?.email}</td>
                  </tr>
                  <tr>
                    <th>Username</th>
                    <td>{employee?.username}</td>
                  </tr>
                  <tr>
                    <th>Mobile Number</th>
                    <td>{employee?.mobile}</td>
                  </tr>
                  <tr>
                    <th>Basic Salary</th>
                    <td>{salary?.basic}</td>
                  </tr>
                  <tr>
                    <th>HRA</th>
                    <td>{salary?.hra}</td>
                  </tr>
                  <tr>
                    <th>Medical Allowance</th>
                    <td>{salary?.medicalAllowance}</td>
                  </tr>
                  <tr>
                    <th>Conveyance Allowance</th>
                    <td>{salary?.conveyanceAllowance}</td>
                  </tr>
                  <tr>
                    <th>Book Allowance</th>
                    <td>{salary?.bookAllowance}</td>
                  </tr>
                  <tr>
                    <th>Special Periodic Allowance</th>
                    <td>{salary?.specialPeriodicAllowance}</td>
                  </tr>
                  <tr>
                    <th>Employer PF</th>
                    <td>{salary?.employerPf}</td>
                  </tr>
                  <tr>
                    <th>PLI</th>
                    <td>{salary?.pli}</td>
                  </tr>
                  <tr>
                    <th>others</th>
                    <td>{salary?.others}</td>
                  </tr>
                  <tr>
                    <th>Total Leaves</th>
                    <td>{salary?.totalLeaves}</td>
                  </tr>
                  <tr>
                    <th>Employee PF</th>
                    <td>{salary?.employeePf}</td>
                  </tr>
                  <tr>
                    <th>TDS</th>
                    <td>{salary?.tds}</td>
                  </tr>
                  <tr>
                    <th>PT</th>
                    <td>{salary?.pt}</td>
                  </tr>
                  <tr>
                    <th>Other Deductions</th>
                    <td>{salary?.otherDeduction}</td>
                  </tr>
                  <tr>
                    <th>Total Deduction</th>
                    <td>{salary?.totalDeduction}</td>
                  </tr>
                  <tr>
                    <th>Last Updated</th>
                    <td>{salary?.assignedDate}</td>
                  </tr>
                  <tr>
                    <th>Payable Salary</th>
                    <td>{salary?.payableSalary || getSavedPayableSalary()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="section">
            <HeaderSection title="Update Salary" />
            <div className="card">
              <div className="card-body pr-5 pl-5 m-1">
                <form className="row" onSubmit={onSubmit} id="addUserForm">
                  {/* Basic salary fields */}
                  {Object.keys(initialState)
                    .filter((key) => !["tds", "totalDeduction"].includes(key))
                    .map((key) => (
                      <div key={key} className="form-group col-md-6">
                        <label>
                          {key.replace(/([A-Z])/g, " $1").toUpperCase()}
                        </label>
                        <div className="input-group">
                          <div className="input-group-prepend">
                            <div className="input-group-text">
                              <i className="fas fa-pen"></i>
                            </div>
                          </div>
                          <input
                            onChange={inputEvent}
                            value={formData[key]}
                            type="number"
                            name={key}
                            className="form-control"
                          />
                        </div>
                      </div>
                    ))}

                  {/* TDS as a checkbox and conditionally displayed input */}
                  <div className="form-group col-md-6">
                    <label>TDS APPLICABLE</label>
                    <div className="input-group">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="tdsApplicable"
                          checked={isTdsApplicable}
                          onChange={handleTdsChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="tdsApplicable">
                          Apply TDS
                        </label>
                      </div>
                    </div>

                    {isTdsApplicable && (
                      <div className="input-group mt-2">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-pen"></i>
                          </div>
                        </div>
                        <input
                          onChange={inputEvent}
                          value={formData.tds}
                          type="number"
                          name="tds"
                          className="form-control"
                          placeholder="Enter TDS amount"
                        />
                      </div>
                    )}
                  </div>

                  {/* Read-only Total Deduction field */}
                  <div className="form-group col-md-6">
                    <label>TOTAL DEDUCTION</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">
                          <i className="fas fa-calculator"></i>
                        </div>
                      </div>
                      <input
                        value={calculateTotalDeduction()}
                        type="number"
                        className="form-control"
                        readOnly
                      />
                    </div>
                    <small className="text-muted">
                      Auto-calculated: Employee PF + TDS + Other Deductions
                    </small>
                  </div>

                  <div className="form-group text-center col-md-12">
                    <button
                      className="btn btn-primary btn-lg"
                      type="submit"
                      style={{ width: "30vh" }}>
                      Update Salary
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </section>
        </div>
      ) : (
        <Loading />
      )}
    </>
  );
};

export default SalaryView;
